import type { Banner } from '~~/server/types/directus';
import type { EventBadgeFeedItem } from '~~/server/types/eventBadges';
import type { GroupedLinks } from '~~/server/utils/repositories/links.repository';
import type { Navigation } from '~~/server/types/navigation';
import type { StoredSiteConfig } from '~~/server/utils/repositories/settings.repository';

export type InitialDataResponse = {
  navigations: Navigation[];
  links: GroupedLinks;
  banners: Banner[];
  eventBadges: EventBadgeFeedItem[];
  portalSettings: StoredSiteConfig | null;
};

type CachedInitialData = InitialDataResponse & { fetchedAt: number };

const MAX_CACHE_AGE_MS = 1000 * 60 * 2;

export async function usePortalInitialData(language: string) {
  const { data, error } = await useAsyncData<CachedInitialData>(
    `init-data:${language}`,
    async () => {
      const result = await $fetch<InitialDataResponse>(`/api/_internal/init/${language}`, {
        method: 'GET',
        retry: 1,
        retryDelay: 500,
        timeout: import.meta.server ? 1000 : 5000,
      });
      return { ...result, fetchedAt: Date.now() };
    },
    {
      getCachedData(key, nuxtApp) {
        const cached = nuxtApp.payload.data[key] || nuxtApp.static.data[key];
        if (!cached) return undefined;
        if (cached.fetchedAt && Date.now() - cached.fetchedAt > MAX_CACHE_AGE_MS) return undefined;
        return cached;
      },
    },
  );

  if (error.value || !data.value) {
    console.error('Error fetching initial data:', error.value);
    return null;
  }

  return data.value;
}
